/**
 * Presentation helpers for the patient Notes tab's table (NotesSection).
 *
 * Split out of the component so the date/time formatting and the sort ordering
 * can be unit-tested without mounting NotesSection, which pulls in the note
 * detail panel, the expanded viewer and Tiptap.
 */
import { getTimezoneAbbreviation, pacificToWallClock } from '@/lib/facilityTimezone';
import type { PatientNote } from '@/v5/services/notes.service';

/**
 * Split a note's Pacific-stored timestamp into the two lines the Date column
 * stacks — "Aug 27, 2026" over "3:04 PM PDT" — both in the viewer's zone.
 *
 * The resolved wall clock is re-anchored to UTC before formatting on purpose.
 * `new Date('2026-08-27T15:04:00')` parses in the *browser's* zone, so handing
 * that instant to `toLocaleString({ timeZone: userTz })` converts it a second
 * time, and a staffer whose preferred zone differs from their browser's reads
 * the wrong clock. Anchoring to UTC and formatting in UTC prints exactly the
 * components `pacificToWallClock` already resolved. The abbreviation still
 * comes from `userTz`, so it names the zone the time is actually in.
 *
 * `time` comes back empty when the timestamp will not parse; the caller then
 * shows the raw string on the date line rather than dropping the value.
 */
export function formatNoteDateTime(
  noteDate: string,
  userTz: string,
): { date: string; time: string } {
  const raw = noteDate.replace(' ', 'T');
  const pacDate = raw.slice(0, 10);
  const pacTime = raw.slice(11, 16) || '00:00';
  const wall = pacificToWallClock(pacDate, pacTime, userTz);
  const dt = new Date(`${wall.date}T${wall.time}:00Z`);
  if (Number.isNaN(dt.getTime())) return { date: noteDate, time: '' };
  return {
    date: dt.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: `${dt.toLocaleTimeString('en-US', {
      timeZone: 'UTC',
      hour: 'numeric',
      minute: '2-digit',
    })} ${getTimezoneAbbreviation(userTz, dt)}`,
  };
}

/**
 * Sort value per sortable Notes column.
 *
 * DataTable only sorts for itself when a column declares no `render`
 * (DataTable.tsx L335), and every Notes column renders, so NotesSection sorts
 * its own rows and drives the table in `manualSort` mode.
 *
 * `noteDate` sorts on the raw Pacific string rather than the rendered
 * viewer-zone clock: the same-instant mapping between zones is monotonic, so
 * string order and displayed order agree, and nothing parses a Date per
 * comparison.
 */
export const NOTE_SORT_VALUES: Record<string, (n: PatientNote) => string | number> = {
  noteId: (n) => n.noteId,
  noteDate: (n) => n.noteDate ?? '',
  typeName: (n) => n.typeName.toLowerCase(),
  authorName: (n) => n.authorName.toLowerCase(),
  title: (n) => n.title.toLowerCase(),
  visibleToPatient: (n) => (n.visibleToPatient ? 1 : 0),
};

/**
 * Order the rows the Notes table shows. Returns the input untouched for a key
 * with no accessor, so an unknown `sortKey` degrades to the API's own order
 * instead of throwing.
 *
 * `Array.prototype.sort` is stable, so rows that tie on the sort value keep the
 * API's newest-first order rather than shuffling between renders.
 */
export function sortNotes(
  notes: PatientNote[],
  sortKey: string,
  sortDir: 'asc' | 'desc',
): PatientNote[] {
  const valueOf = NOTE_SORT_VALUES[sortKey];
  if (!valueOf) return notes;
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...notes].sort((a, b) => {
    const av = valueOf(a);
    const bv = valueOf(b);
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
  });
}
